import { toast } from "sonner";

const FIELD_NAME_MAP: Record<string, string> = {
  item_id: "Product Code",
  book_title: "Book Title",
  sr_no: "Serial Number",
  is_active: "Status",
  email: "Email",
  password: "Password",
  phone: "Phone Number",
  city: "City",
  state: "State",
  country: "Country",
  sort_order: "Sort Order",
  image_url: "Image URL",
  link_url: "Link URL",
};

const getFriendlyFieldName = (key: string): string => {
  return (
    FIELD_NAME_MAP[key] ||
    key
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

/**
 * Deeply searches an object or string for ErrorDetail patterns and extracts the first message found.
 */
const deepExtractError = (obj: any): string | null => {
  if (typeof obj === "string") {
    // Look for string='...' or string="..." with optional spaces
    const match = obj.match(/string\s*=\s*['"]([^'"]+)['"]/);
    if (match) {
      return match[1];
    }

    // Fallback: if it's a raw ErrorDetail string but regex failed
    if (obj.includes("ErrorDetail")) {
      const parts = obj.split(/['"]/);
      const possible = parts.find(
        p => p.length > 5 && (p.includes(" ") || p.endsWith("."))
      );
      if (possible) {
        return possible;
      }
    }
    return null;
  }

  if (obj && typeof obj === "object") {
    // Check all values in the object/array
    const values = Array.isArray(obj) ? obj : Object.values(obj);
    for (const val of values) {
      const extracted = deepExtractError(val);
      if (extracted) {
        return extracted;
      }
    }
  }

  return null;
};

/**
 * Parses an error object (likely from Axios) and returns a human-readable string.
 */
export const formatError = (error: any): string => {
  if (typeof error === "string") {
    return error;
  }

  const data = error?.response?.data;

  const formatWithFieldName = (key: string, message: string): string => {
    const friendlyKey = getFriendlyFieldName(key);
    // If message already starts with the field name (case insensitive), avoid duplication
    if (message.toLowerCase().startsWith(friendlyKey.toLowerCase())) {
      return message;
    }
    return `${friendlyKey}: ${message}`;
  };

  // 1. Try deep extraction from data immediately (most robust)
  if (data) {
    const extracted = deepExtractError(data);
    if (extracted) {
      // If we found it in a field, try to find the key to prefix it
      if (typeof data === "object" && !Array.isArray(data)) {
        for (const [key, val] of Object.entries(data)) {
          if (deepExtractError(val) === extracted) {
            return formatWithFieldName(key, extracted);
          }
        }
      }
      return extracted;
    }
  }

  if (!data) {
    return error?.message ?? "An unexpected error occurred";
  }

  // Handle case where data is a raw string (e.g. raw Python dict representation)
  if (typeof data === "string") {
    return data;
  }

  // Handle standard Django Rest Framework 'detail' field
  if (data.detail && typeof data.detail === "string") {
    return data.detail;
  }

  // Handle field-level errors (usually an object with arrays of messages)
  if (typeof data === "object") {
    // If it's a nested detail object
    if (data.detail && typeof data.detail === "object") {
      const firstKey = Object.keys(data.detail)[0];
      const val = data.detail[firstKey];
      const nestedStr = Array.isArray(val) ? val[0] : val;
      return formatWithFieldName(firstKey, String(nestedStr));
    }

    const keys = Object.keys(data);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const val = data[firstKey];
      const valToShow = Array.isArray(val) ? val[0] : val;

      if (typeof valToShow === "string") {
        return formatWithFieldName(firstKey, valToShow);
      }

      if (typeof valToShow === "object" && valToShow?.message) {
        return formatWithFieldName(firstKey, valToShow.message);
      }

      return JSON.stringify(data);
    }
  }

  return error?.message ?? "An unexpected error occurred";
};

/**
 * Displays a toast with a formatted error message.
 */
export const showErrorToast = (error: any, fallback?: string) => {
  const message = formatError(error);
  // If the formatted message is generic and we have a better fallback, use fallback
  if (
    (message === "An unexpected error occurred" ||
      message === "Request failed with status code 400" ||
      message === "Request failed with status code 500") &&
    fallback
  ) {
    toast.error(fallback);
  } else {
    toast.error(message);
  }
};
